import {Component, ElementRef, EventEmitter, Input, Output, OnInit, ViewChild} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {SelectionModel} from '@angular/cdk/collections';
import {MatTableDataSource} from '@angular/material/table';
import * as _ from 'lodash';
import {HighchartComponent} from '../../../utils/highchart/highchart.component';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ThresholdApiService} from '../../../services/api/statistics/threshold/threshold-api.service';
import {DateType} from '../../enums/date-type.enum';
import {DateTimeUnitTypes} from '../../enums/date-time-unit-types.enum';
import {OverTimeGraphType} from '../../enums/over-time-graph-type.enum';
import {ReportFilterType} from '../../enums/report-filter-type.enum';
import {StatisticsApiService} from '../../../services/api/statistics/statistics-api.service';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';

const THRESHOLD_SCHEMA = {
  statisticType: 'select',
  Threshold: 'number',
  lowWatermark: 'number',
  highWatermark: 'number'
};

@Component({
  selector: 'thresholds-configuration',
  templateUrl: './thresholds-configuration.component.html',
  styleUrls: ['./thresholds-configuration.component.scss']
})
export class ThresholdsConfigurationComponent implements OnInit {

  MAX_ITEMS_FOR_STATISTIC = 100;

  @ViewChild('statisticTable', {static: false}) private statisticTable: ElementRef;
  @Output() onChange = new EventEmitter<string>();
  @ViewChild('statistics', {static: false}) private statistics: HighchartComponent;
  @Input() set selectedThreshold(selectedThreshold: any) {
    this.threshold = selectedThreshold ? selectedThreshold : null;

    this.initializeThreshold();
  }

  defaultChartOptions = {
    animation: true,
    exporting: false,
    interval: 300000,
    parentType: 'LINE',
    series: [],
    stacked: false,
    startDate: undefined,
    subTitle: 'Thresholds Statistics',
    title: {text: 'Thresholds Statistics', fontSize: '12px'},
    type: 'line'
  };

  chartOptions = _.cloneDeep(this.defaultChartOptions);

  structure;
  elementTypeOptions = [];
  alertTypeOptions = [
    {value: 'indeterminate', label: 'Indeterminate'},
    {value: 'warning', label: 'Warning'},
    {value: 'minor', label: 'Minor'},
    {value: 'major', label: 'Major'},
    {value: 'critical', label: 'Critical'}
  ];

  threshold = null;
  isAuthorized: boolean;
  displayedColumns = [
    'statisticType', 'highWatermark', 'lowWatermark', '$$edit'
  ];

  displayedColumnsNames = {
    statisticType: 'Statistic Type',
    highWatermark: 'Trigger Threshold',
    lowWatermark: 'Clear Threshold'
  };
  dataSchema = THRESHOLD_SCHEMA;
  statisticsOptions = [];
  availableStatisticsOptions = [];
  currentTypeConfiguration;

  dataSource = new MatTableDataSource<any>();
  selection = new SelectionModel<any>(false, []);

  elementOptions = [];
  externalData: any = {};

  watermarkValidators = [Validators.min(1), Validators.required, Validators.pattern(/^[0-9]\d*$/)];
  watermarkControls = {
    lowWatermark: new FormControl(null, this.watermarkValidators),
    highWatermark: new FormControl(null, this.watermarkValidators)
  };

  constructor(private thresholdApiService: ThresholdApiService,
              private statisticsApiService: StatisticsApiService,
              private userPrivilege: UserPrivilegeService,
              private activatedRoute: ActivatedRoute,
              private dialog: MatDialog,
              private router: Router) { }

  ngOnInit(): void {
    this.isAuthorized = this.userPrivilege.isAuthorized();
    this.structure = this.activatedRoute.snapshot.data.structure;
    this.elementTypeOptions = this.structure.map(statisticConfiguration => {
      return {
        value: statisticConfiguration.elementType,
        label: statisticConfiguration.elementName
      };
    });

    this.thresholdApiService.getExternalData().then(data => {
      this.externalData = {
        node: data[0],
        pcon: this.generatePConsData(data[1]),
        connection: data[2].elements,
        routingGroup: data[3],
        customer: data[4].elements,
        voipPeer: data[5].elements,
        router: data[6],
        routingRule: this.generateRoutingRulesData(data[3])
      };

      this.initializeThreshold();
    });
  }

  onElementTypeChange($event: any): void {
    if (_.isNil(this.structure)) {
      return;
    }
    this.currentTypeConfiguration = this.structure.find(configuration => configuration.elementType === $event);
    this.statisticsOptions = this.currentTypeConfiguration.supportedStatistics;
    this.availableStatisticsOptions = _.cloneDeep(this.statisticsOptions);
    this.dataSource.data = this.threshold.thresholds;
    this.elementOptions = this.getElements();
    this.setDefaultChartOptions();
  }

  displaySelectedStatistic(item = null): void {
    let isElementsCrossedLimit = false;

    if (this.selection.selected.length === 0 ||
        (this.threshold.elementType !== 'arm' && this.threshold.elementIds.length === 0 && !this.threshold.allElements)) {
      this.setDefaultChartOptions();
      return;
    }
    const selectedItem = item || this.selection.selected[0];
    const type = this.structure.find(structureItem => structureItem.elementType === this.threshold.elementType);
    const statistic = type.supportedStatistics.find(s => s.statisticType === selectedItem.statisticType);

    if (_.isNil(statistic)) {
      return;
    }

    let elements = (this.threshold.allElements && this.elementOptions)
        ? this.elementOptions.map(element => element.id) : this.threshold.elementIds;

    if (elements && (elements.length > this.MAX_ITEMS_FOR_STATISTIC)) {
      elements = elements.slice(0, this.MAX_ITEMS_FOR_STATISTIC);
      isElementsCrossedLimit = true;
    }

    const parameters = {
      reportIdentifier: statistic.reportIdentifier,
      dateType: DateType.RELATIVE,
      relativeTimeUnit: DateTimeUnitTypes.HOUR,
      relativeTimeUnitValue: 3,
      overTimeGraphType: OverTimeGraphType.MULTIPLE_ELEMENTS,
      availableFilters: [
        ReportFilterType.DATE,
        ReportFilterType.SINGLE_STATISTIC,
        ReportFilterType.ELEMENTS,
        ReportFilterType.OVER_TIME_GRAPH_TYPE
      ],
      singleStatistic: selectedItem.statisticType,
      elements
    };

    this.statisticsApiService.getReportForChart(parameters).then((reports => {
      this.setRoutingAttemptsReport(reports, selectedItem, isElementsCrossedLimit);
    })).catch(() => {});
  }

  submitStatistic(element): void {
    this.updateStatisticsOptions();

    element.isEdit = false;
  }

  onAllElementsChanged(): void {
    if (this.threshold.allElements) {
      this.threshold.elementIds = [];
    }
  }

  resetThreshold(): any {
    this.threshold.allElements = false;
    this.threshold.thresholds = [];
    this.threshold.elementIds = [];
  }

  addStatistic(): void {
    const defaultStatistic = {
      highWatermark: 50,
      lowWatermark: 50,
      statisticType: this.availableStatisticsOptions[0].statisticType
    };

    this.threshold.thresholds.push(defaultStatistic);
    this.selection.select(defaultStatistic);
    this.displaySelectedStatistic(defaultStatistic);
    this.dataSource.data = this.threshold.thresholds;
    this.updateStatisticsOptions();
    this.statisticTableScrollToBottom();
  }

  isAddStatisticEnabled(): boolean {
    return this.availableStatisticsOptions.length > 0 && !this.isStatisticInEditMode();
  }

  deleteStatistic(): void {
    if (this.selection.selected && this.selection.selected[0] && this.selection.selected[0].statisticType) {
      const confirmationMessage = `Are you sure you want to delete: "${this.getStatisticName(this.selection.selected[0].statisticType)}" ?`;

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: confirmationMessage
        }
      });

      dialogRef.componentInstance.onApply.subscribe(() => {
        const selectionIndex = this.threshold.thresholds.indexOf(this.selection.selected[0]);
        this.threshold.thresholds = this.threshold.thresholds.filter(threshold =>
            threshold.statisticType !== this.selection.selected[0].statisticType);

        this.dataSource.data = this.threshold.thresholds;
        this.updateStatisticsOptions();

        this.selectPreviousItem(selectionIndex);
        dialogRef.close();
      });
    }
  }

  editStatistic(element): void {
    if (element && element.statisticType) {
      this.statisticsSelectionChanged(element.statisticType);
    }
    this.threshold.thresholds.forEach(threshold => {
      threshold.isEdit = false;
    });
    element.isEdit = true;
  }

  isStatisticInEditMode(): boolean {
    return this.threshold.thresholds.some(threshold => threshold.isEdit);
  }

  navigateToAlarms(element): void {
    const currentStatistic = this.currentTypeConfiguration.supportedStatistics.find(s => s.statisticType === element.statisticType);

    this.router.navigate(['/alarms/active', {
      search: `rule#${this.threshold.name}/Statistic#${currentStatistic.statisticName}`
    }]);
  }

  getStatisticName(statisticValue): string {
    return this.statisticsOptions.find(statistic => statistic.statisticType === statisticValue).statisticName;
  }

  isValidStatistic(element): boolean {
    return (parseInt(element.lowWatermark, 10) <= parseInt(element.highWatermark, 10));
  }

  onSubmit(): void {
    if (this.threshold.allElements && this.threshold.elementIds.length > 0) {
      this.threshold.elementIds = [];
    }

    if (_.isNil(this.threshold.id)) {
      this.thresholdApiService.addThreshold(this.threshold).then(() => {
        this.onChange.emit(this.threshold);
      }).catch(() => {});
    } else {
      this.thresholdApiService.editThreshold(this.threshold.id, this.threshold).then((threshold: any) => {
        this.onChange.emit(threshold);
      }).catch(() => {});
    }
  }

  private getElements(): any {
    if (!this.threshold.elementType && _.isEmpty(this.externalData)) {
      return [];
    }

    return this.externalData[this.threshold.elementType];
  }

  private updateStatisticsOptions(): void {
    const statisticsTypeInUsed = this.threshold.thresholds.map(t => t.statisticType);

    this.availableStatisticsOptions = this.statisticsOptions.filter(s => statisticsTypeInUsed.indexOf(s.statisticType) === -1);
  }

  private statisticsSelectionChanged(currentStatisticType): void {
    const statisticsTypeInUsed = this.threshold.thresholds.map(t => t.statisticType);

    this.availableStatisticsOptions = this.statisticsOptions.filter(s => s.statisticType === currentStatisticType ||
        statisticsTypeInUsed.indexOf(s.statisticType) === -1);
  }

  private setRoutingAttemptsReport(reports, selectedItem, isElementsCrossedLimit): void {
    const numberOfPoints = this.getNumberOfPoints(reports.series);

    if (selectedItem.lowWatermark) {
      this.addWatermark(reports, selectedItem.lowWatermark, numberOfPoints, 'Clear threshold', 'green');
    }

    if (selectedItem.highWatermark) {
      this.addWatermark(reports, selectedItem.highWatermark, numberOfPoints, 'Trigger threshold');
    }
    this.chartOptions.subTitle = reports.subTitle;
    this.chartOptions.title.text = `${reports.title}${isElementsCrossedLimit ? ' (First 100 items)' : ''}`;
    this.chartOptions.series = reports.series;
    this.chartOptions.startDate = reports.startDate;
    this.chartOptions.interval = reports.interval;

    this.statistics.setOptions(this.chartOptions);
  }

  private initializeThreshold(): void {
    if (_.isNil(this.threshold) || _.isEmpty(this.externalData)) {
      return;
    }

    this.onElementTypeChange(this.threshold.elementType);
    if (this.threshold.thresholds && this.threshold.thresholds[0]) {
      this.selectAndDisplayStatistic(0);
      this.updateStatisticsOptions();
    } else {
      this.setDefaultChartOptions();
    }

    this.statisticTableScrollToTop();
  }

  private statisticTableScrollToTop(): void {
    try {
      this.statisticTable.nativeElement.scrollTop = 0;
    } catch (err) {}
  }

  private setDefaultChartOptions(): void {
    if (!_.isNil(this.statistics)) {
      this.statistics.setOptions(this.defaultChartOptions);
    }
  }

  private generatePConsData(pCons: any): any {
    return pCons.elements.map(pCon => {
      return {
        name: `${pCon.name} (${pCon.nodeName})`,
        id: pCon.id
      };
    });
  }

  private generateRoutingRulesData(routingGroups: any): any {
    const routingRule = [];

    routingGroups.forEach(rg => {
      const rrOptions = rg.routingRules.map((rr) => {
        return {
          name: `${rr.name} (${rg.name})`,
          id: rr.id
        };
      });

      routingRule.push(...rrOptions);
    });

    return routingRule;
  }

  private addWatermark(reports, watermark, numberOfPoints, label, color = 'red'): void {
    const watermarkValue = (parseInt(watermark, 10));
    const lowWatermarkData = Array(numberOfPoints).fill({ y: watermarkValue });

    reports.series.push({
      data: lowWatermarkData,
      label: {enabled: true, onArea: false, style: {fontWeight: 100, 'font-size': 10}},
      enableMouseTracking: false,
      name: `${label}: ${watermarkValue}`,
      marker: false,
      color
    });
  }

  private selectPreviousItem(selectionIndex: any): void {
    if (selectionIndex === 0) {
      if (_.isEmpty(this.threshold.thresholds)) {
        this.setDefaultChartOptions();
        this.selection.clear();
      } else {
        this.selectAndDisplayStatistic(0);
      }
    } else {
      this.selectAndDisplayStatistic(selectionIndex - 1);
    }
  }

  private selectAndDisplayStatistic(index): void {
    this.selection.select(this.threshold.thresholds[index]);
    this.displaySelectedStatistic(this.threshold.thresholds[index]);
  }

  private statisticTableScrollToBottom(): void {
    try {
      this.statisticTable.nativeElement.scrollTop =
          this.statisticTable.nativeElement.scrollHeight;
    } catch (err) {}
  }

  private getNumberOfPoints(series): number {
    return series.reduce((a, b) => Math.max(a, this.getSeriesLength(b)), 20);
  }

  private getSeriesLength(series): number {
    return series.data && series.data.length
        ? series.data.length : 0;
  }

  onStatChange(event: any): void {
    this.displaySelectedStatistic();
  }
}
